import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { withTranslation } from 'react-i18next';
import axios from 'axios';

const ServiceBayFormDialog = ({
  t,
  open,
  onClose,
  serviceBay,
  onCreated,
  onUpdated,
  onDeleted,
}) => {
  const isEditMode = Boolean(serviceBay);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    if (serviceBay) {
      setFormData({
        name: serviceBay.name || '',
        description: serviceBay.description || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
      });
    }
    setError(null);
    setConfirmDelete(false);
  }, [serviceBay, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError(t('serviceBays.nameRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
      };

      let response;
      if (isEditMode) {
        response = await axios.patch(`/api/scheduling/service-bays/${serviceBay.id}/`, payload);
        if (onUpdated) {
          onUpdated(response.data);
        }
      } else {
        response = await axios.post('/api/scheduling/service-bays/', payload);
        if (onCreated) {
          onCreated(response.data);
        }
      }

      onClose();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.name) {
        setError(errorData.name[0]);
      } else if (errorData?.non_field_errors) {
        setError(errorData.non_field_errors[0]);
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    setDeleteLoading(true);
    try {
      await axios.delete(`/api/scheduling/service-bays/${serviceBay.id}/`);
      if (onDeleted) {
        onDeleted(serviceBay.id);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
          {isEditMode ? t('serviceBays.editServiceBay') : t('serviceBays.addServiceBay')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            name="name"
            label={t('serviceBays.name')}
            placeholder={t('serviceBays.namePlaceholder')}
            value={formData.name}
            onChange={handleChange}
            required
            fullWidth
            autoFocus
            error={Boolean(error && !formData.name.trim())}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          <TextField
            name="description"
            label={t('serviceBays.descriptionLabel')}
            placeholder={t('serviceBays.descriptionPlaceholder')}
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: isEditMode ? 'space-between' : 'flex-end' }}>
        {isEditMode && (
          <Box>
            <Button
              color="error"
              variant={confirmDelete ? 'contained' : 'outlined'}
              onClick={handleDelete}
              disabled={deleteLoading}
              startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
              sx={{ borderRadius: '10px' }}
            >
              {confirmDelete ? <span>{t('Confirm delete')}</span> : <span>{t('Delete')}</span>}
            </Button>
            {confirmDelete && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {t('serviceBays.deleteWarning')}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              borderRadius: '10px',
              px: 3,
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              borderRadius: '10px',
              px: 3,
              bgcolor: 'secondary.main',
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            {loading ? <span>{t('Saving...')}</span> : isEditMode ? <span>{t('Save Changes')}</span> : <span>{t('serviceBays.addServiceBay')}</span>}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(ServiceBayFormDialog);
